import React, { useState, useEffect, useMemo } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Table from '../../components/common/Table/Table';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import { getAllManagers, createManager, deleteManager, suspendManager, unsuspendManager } from '../../services/managerService';
import useForm from '../../hooks/useForm';
import { useToast, useConfirm } from '../../context/ToastContext';
import styles from './Mentors.module.css';

const Managers = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      const data = await getAllManagers();
      setManagers(data);
    } catch (error) {
      console.error('Error fetching managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const { values, handleChange, handleSubmit, resetForm , isSubmitting} = useForm(
    {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      department: '',
      position: ''
    },
    async (formValues) => {
      try {
        const result = await createManager(formValues);
        setShowCredentials(result.credentials);
        setShowModal(false);
        resetForm();
        fetchManagers();
      } catch (error) {
        toast.error('Error creating manager: ' + error.message);
      }
    }
  );

  const handleDelete = async (managerId) => {
    const confirmed = await confirm('Are you sure you want to delete this manager?', { confirmText: 'Delete', variant: 'danger' });
    if (confirmed) {
      try {
        await deleteManager(managerId);
        fetchManagers();
      } catch (error) {
        toast.error('Error deleting manager: ' + error.message);
      }
    }
  };

  const handleSuspend = async (userId, isSuspended) => {
    const action = isSuspended ? 'unsuspend' : 'suspend';
    const confirmMessage = isSuspended 
      ? 'Are you sure you want to unsuspend this manager? They will be able to login again.'
      : 'Are you sure you want to suspend this manager? They will not be able to login.';
    
    const confirmed = await confirm(confirmMessage, { confirmText: isSuspended ? 'Unsuspend' : 'Suspend', variant: 'warning' });
    if (confirmed) {
      try {
        if (isSuspended) {
          await unsuspendManager(userId);
        } else {
          await suspendManager(userId, 'Suspended by administrator');
        }
        fetchManagers();
        toast.success(`Manager ${action}ed successfully`);
      } catch (error) {
        toast.error(`Error ${action}ing manager: ${error.message}`);
      }
    }
  };

  // Filter managers based on search query
  const filteredManagers = useMemo(() => {
    if (!searchQuery) return managers;
    
    const query = searchQuery.toLowerCase();
    return managers.filter(manager => {
      const fullName = `${manager.first_name} ${manager.last_name}`.toLowerCase();
      const email = manager.email?.toLowerCase() || '';
      return fullName.includes(query) || email.includes(query);
    });
  }, [managers, searchQuery]);

  const columns = [
    { 
      header: 'Name', 
      accessor: 'first_name',
      sortKey: 'first_name',
      sortValue: (row) => `${row.first_name} ${row.last_name}`,
      render: (row) => (
        <div>
          {`${row.first_name} ${row.last_name}`}
          {row.is_suspended && <span style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '11px', background: '#ff9800', color: 'white', borderRadius: '4px', fontWeight: 'bold' }}>SUSPENDED</span>}
        </div>
      )
    },
    { header: 'Email', accessor: 'email' },
    { header: 'Phone', accessor: 'phone', sortable: false },
    { header: 'Department', accessor: 'department', sortable: false },
    { header: 'Position', accessor: 'position', sortable: false },
    { 
      header: 'Actions',
      sortable: false,
      render: (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button size="small" 
            variant={row.is_suspended ? 'primary' : 'warning'} 
            onClick={() => handleSuspend(row.user_id, row.is_suspended)} loading={loading}
            style={{ 
              backgroundColor: row.is_suspended ? '#4CAF50' : '#FF9800',
              minWidth: '90px'
            }}
          >
            {row.is_suspended ? 'Unsuspend' : 'Suspend'}
          </Button>
          <Button size="small" variant="danger" onClick={() => handleDelete(row.id)} loading={loading}>
            Delete
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.mentorsPage}>
      <div className={styles.header}>
        <h1>Managers</h1>
        <div className={styles.headerActions}>
          <SearchBar 
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search managers by name or email..."
          />
          <Button icon="+" onClick={() => setShowModal(true)} loading={loading}>Add Manager</Button>
        </div>
      </div>

      <Card>
        <Table columns={columns} data={filteredManagers} />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add New Manager">
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>First Name *</label>
              <input name="first_name" value={values.first_name} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Last Name *</label>
              <input name="last_name" value={values.last_name} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Email *</label>
              <input type="email" name="email" value={values.email} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Phone *</label>
              <input name="phone" value={values.phone} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Department</label>
              <input name="department" value={values.department} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Position</label>
              <input name="position" value={values.position} onChange={handleChange} />
            </div>
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setShowModal(false)} loading={loading}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Create Manager</Button>
          </div>
        </form>
      </Modal>

      {showCredentials && (
        <Modal isOpen={!!showCredentials} onClose={() => setShowCredentials(null)} title="Manager Credentials">
          <div className={styles.credentials}>
            <p><strong>Username:</strong> {showCredentials.username}</p>
            <p><strong>Email:</strong> {showCredentials.email}</p>
            <p><strong>Password:</strong> {showCredentials.password}</p>
            <p className={styles.warning}>Alert: Please save these credentials securely. They will not be shown again.</p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Managers;

